import { useEffect, useMemo, useState } from "react";

// Sélection multiple pour les tableaux react-bootstrap-table dont la clé est
// `uid`. La sélection est conservée d'une page à l'autre, et purgée des lignes
// qui ont disparu après un rechargement : sinon une action groupée porterait
// sur des factures qui ne sont plus à l'écran.
export function useRowSelection<T extends { uid: string }>(rows: T[]) {
  const [selected, setSelected] = useState<string[]>([]);

  useEffect(() => {
    const present = new Set(rows.map((r) => r.uid));
    setSelected((prev) => {
      const kept = prev.filter((uid) => present.has(uid));
      // Renvoyer `prev` tel quel quand rien n'a disparu : sinon un parent qui
      // recrée son tableau à chaque rendu déclencherait une boucle de rendus.
      return kept.length === prev.length ? prev : kept;
    });
  }, [rows]);

  const selectedRows = useMemo(() => {
    const ids = new Set(selected);
    return rows.filter((r) => ids.has(r.uid));
  }, [rows, selected]);

  const clear = () => setSelected([]);

  const selectRow = {
    mode: "checkbox" as const,
    // Pas de sélection au clic sur la ligne : elle porte déjà des actions
    // (voir, commenter) qui entreraient en conflit.
    clickToSelect: false,
    selected,
    onSelect: (row: T, isSelect: boolean) => {
      setSelected((prev) =>
        isSelect ? [...prev, row.uid] : prev.filter((uid) => uid !== row.uid)
      );
      return true;
    },
    // Case d'en-tête : agit sur les lignes visibles de la page courante, après
    // filtres — c'est le comportement de la bibliothèque.
    onSelectAll: (isSelect: boolean, visibles: T[]) => {
      const ids = visibles.map((r) => r.uid);
      setSelected((prev) =>
        isSelect
          ? Array.from(new Set([...prev, ...ids]))
          : prev.filter((uid) => !ids.includes(uid))
      );
    },
  };

  return { selected, setSelected, selectedRows, selectRow, clear };
}
